import { resolveVenueFrame, venueOffsetMinutesAt } from 'functions/venueTimeFrame';
import { isActiveProviderAdmin } from 'services/authentication/isProviderAdmin';
import { downloadJSON, downloadText } from 'services/export/download';
import { localizeReportTimes } from './localizeReportTimes';
import { resolveAuditBaseUrl } from './resolveAuditBaseUrl';
import { openStructureAuditModal } from './structureAudit';
import { tournamentEngine } from 'services/factory/engine';
import { createReportsTable } from './createReportsTable';
import { exportReportPDF } from './exportReportPDF';
import { controlBar } from 'courthive-components';
import { context } from 'services/context';
import { env } from 'settings/env';
import axios from 'axios';

import { LEFT, REPORTS_CONTROL, REPORTS_TAB, RIGHT, TOURNAMENT, TOURNAMENT_REPORTS } from 'constants/tmxConstants';
import { t } from 'i18n';

let activeReport: any;
let activeReportName = '';

// Remembers the last report the user viewed so it re-opens across tab/tournament
// navigation instead of always resetting to the first report.
const SELECTED_REPORT_KEY = 'tmx_selectedReport';
const getStoredReportId = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_REPORT_KEY);
  } catch {
    return null;
  }
};
const storeReportId = (reportId: string): void => {
  try {
    localStorage.setItem(SELECTED_REPORT_KEY, reportId);
  } catch {
    /* localStorage unavailable (private mode / quota) — selection just won't persist */
  }
};

// Reflect the current report in the URL (…/reports/:reportId) so it can be
// bookmarked and deep-linked. Replace (not push) and skip the route handler so
// updating the address bar never re-renders the tab.
const syncReportUrl = (reportId: string): void => {
  const tournamentId = tournamentEngine.getTournament()?.tournamentRecord?.tournamentId;
  if (!tournamentId) return;
  context.router?.navigate(`/${TOURNAMENT}/${tournamentId}/${REPORTS_TAB}/${reportId}`, {
    callHandler: false,
    historyAPIMethodName: 'replaceState',
    updateBrowserURL: true,
  });
};

// admin/director-only structure-integrity audit launcher for the reports control bar
const auditControlItem = () => ({
  onClick: () => openStructureAuditModal(),
  label: t('reports.audit'),
  intent: 'is-warning',
  location: RIGHT,
  id: 'structureAudit',
});

export function renderReportsTab(options: { reportId?: string } = {}): void {
  const reportContainer = document.getElementById(TOURNAMENT_REPORTS);
  const controlTarget = document.getElementById(REPORTS_CONTROL);
  if (!reportContainer || !controlTarget) return;

  reportContainer.innerHTML = '';
  controlTarget.innerHTML = '';

  const result: any = tournamentEngine.getAvailableReports();
  const reports = (result?.availableReports ?? []).filter((r: any) => r.computableNow);
  const canAudit = isActiveProviderAdmin();

  if (!reports.length) {
    // admins/directors can still run the structure audit even when no data reports compute
    if (canAudit) controlBar({ target: controlTarget, items: [auditControlItem()] });
    reportContainer.innerHTML = `<div style="padding: 2em; color: var(--tmx-text-secondary);">No reports available for this tournament.</div>`;
    return;
  }

  // Initial selection priority: an explicit report id from the URL (deep link),
  // then the last-viewed report from localStorage, then the first available.
  const findById = (id?: string | null) => (id ? reports.find((r: any) => r.reportId === id) : undefined);
  const initialReport = findById(options.reportId) ?? findById(getStoredReportId()) ?? reports[0];

  const reportOptions = reports.map((r: any) => ({
    label: r.name,
    value: r.reportId,
    close: true,
    onClick: () => selectReport(r),
  }));

  const items = [
    {
      options: reportOptions,
      label: initialReport.name,
      modifyLabel: true,
      intent: 'is-info',
      location: LEFT,
      id: 'reportSelector',
    },
    {
      onClick: () => activeReport && exportReportPDF({ ...activeReport, reportName: activeReportName }),
      label: 'PDF',
      intent: 'is-info',
      location: RIGHT,
      id: 'exportPDF',
    },
    {
      onClick: () => activeReport && exportCSV(activeReport, activeReportName),
      label: 'CSV',
      intent: 'is-info',
      location: RIGHT,
      id: 'exportCSV',
    },
    {
      onClick: () => activeReport && downloadJSON(`${activeReportName}.json`, activeReport),
      label: 'JSON',
      intent: 'is-info',
      location: RIGHT,
      id: 'exportJSON',
    },
    ...(canAudit ? [auditControlItem()] : []),
  ];

  controlBar({ target: controlTarget, items });

  // Open the restored (or first) report.
  selectReport(initialReport);
}

/**
 * Parameters every factory report is generated with.
 *
 * Both frames are sent. `timeZone` is the IANA identifier of the **venue** —
 * `tournamentRecord.localTimeZone`, with the browser's zone standing in only
 * when the record carries none (see `functions/venueTimeFrame`). It used to be
 * the browser's unconditionally, which is right machinery fed the wrong zone:
 * a director running a Florida event from a Pacific laptop read every call time
 * and variance three hours out. The factory prefers `timeZone` because it
 * yields the offset **per instant**, so a
 * tournament spanning a DST change converts correctly on both sides. A bare
 * `utcOffsetMinutes` is the offset *now* and would be an hour wrong on the far
 * side of the change — silently, in reports measured in minutes.
 *
 * `utcOffsetMinutes` is still sent as the fallback for wrappers that predate the
 * zone parameter, and for the case where the runtime cannot name its zone.
 *
 * Until this existed `generateReport` was called with no parameters at all, so
 * the offset path the factory wrappers already exposed was dead and each report
 * had to localize its own timestamps client-side.
 */
function reportParameters(): Record<string, any> {
  const { timeZone } = resolveVenueFrame();
  return {
    timeZone,
    // The offset of the VENUE zone right now, not the browser's. Only a
    // fallback: any wrapper that reads it instead of `timeZone` gets an offset
    // fixed at this moment, which is why `timeZone` is preferred above.
    utcOffsetMinutes: venueOffsetMinutesAt(new Date(), timeZone) ?? -new Date().getTimezoneOffset(),
  };
}

async function selectReport(report: any): Promise<void> {
  const { reportId, name, source } = report;
  activeReportName = name;
  storeReportId(reportId);
  syncReportUrl(reportId);

  if (source === 'server') {
    await fetchServerReport(reportId);
  } else {
    const parameters = reportParameters();
    const result: any = tournamentEngine.generateReport({ reportId, parameters });
    if (result.error) return;
    localizeReportTimes(result.rows, parameters.timeZone);
    activeReport = { columns: result.columns, rows: result.rows };
    createReportsTable({ columns: result.columns, rows: result.rows });
  }
}

async function fetchServerReport(reportId: string): Promise<void> {
  const { tournamentRecord } = tournamentEngine.getTournament();
  const tournamentId = tournamentRecord?.tournamentId;
  if (!tournamentId) return;

  const baseUrl = resolveAuditBaseUrl(globalThis.location?.origin ?? '', env.auditWorkerUrl);
  const reportType = reportId;

  const reportContainer = document.getElementById(TOURNAMENT_REPORTS);
  if (reportContainer) reportContainer.innerHTML = '<div style="padding: 2em;">Loading audit data...</div>';

  try {
    const response = await axios.post(`${baseUrl}/condense/${tournamentId}/${reportType}`);
    const data = response?.data;
    if (data?.columns && data?.rows) {
      activeReport = { columns: data.columns, rows: data.rows };
      createReportsTable({ columns: data.columns, rows: data.rows });
    } else {
      if (reportContainer)
        reportContainer.innerHTML =
          '<div style="padding: 2em; color: var(--tmx-text-secondary);">No audit data available.</div>';
    }
  } catch (err: any) {
    console.warn('[reports] audit-worker fetch failed:', err.message);
    if (reportContainer)
      reportContainer.innerHTML =
        '<div style="padding: 2em; color: var(--tmx-accent-red);">Audit worker unavailable. Ensure the audit worker is running.</div>';
  }
}

function exportCSV(report: { columns: any[]; rows: any[] }, reportName: string): void {
  const header = report.columns.map((c: any) => c.title).join(',');
  const csvRows = report.rows.map((row: any) =>
    report.columns
      .map((c: any) => {
        let val = row[c.key] ?? '';
        val = String(val);
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          val = '"' + val.replace(/"/g, '""') + '"';
        }
        return val;
      })
      .join(','),
  );
  const csv = [header, ...csvRows].join('\n');
  downloadText(`${reportName}.csv`, csv);
}
